// /communities/:slug/settings — the config-and-people surface for a
// community. Combines four panels that used to live scattered across
// the Newsroom (Topics / Photos / Reporter) and the Dashboard
// (Editors / Boundary link).
//
// PR-A of T3.1 (see docs/issues.md): additive — Newsroom + Dashboard
// keep their inline panels for one release so nothing breaks if
// something got missed here. PR-B rips them out of Newsroom, PR-C
// rips them out of Dashboard.

import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useApi } from '../lib/api';
import { useAuth } from '../lib/auth';
import { timeAgo } from '../lib/format';
import type { Cadence, Community } from '../types/api';

interface Topic {
    slug:                 string;
    name:                 string;
    mode:                 'activity' | 'rewrite';
    icon?:                string | null;
    color?:               string | null;
    active:               boolean;
    default_photo_query?: string | null;
    created_at:           string;
}

interface ReporterSettings {
    agent_id?:      string;
    agent_name?:    string;
    system_prompt?: string;
    model?:         string;
    active?:        boolean;
    paused_reason?: string | null;
    exists:         boolean;
}

interface StockPhoto {
    id:          string;
    media_url:   string;
    caption?:    string | null;
    tags:        string[];
    active:      boolean;
    usage_count: number;
    created_at:  string;
}

interface Moderator {
    user_id:      string;
    display_name: string;
    role:         string;
    granted_at:   string;
}

const REPORTER_PROMPT_TEMPLATE = `You are the news reporter for the {COMMUNITY} community paper.

GEOGRAPHY — be tight:
- RELEVANT: {COMMUNITY} itself and neighbouring suburbs — [list yours here, e.g. Oranjezicht, Gardens, Tamboerskloof, Higgovale, Devil's Peak Estate, CBD].
- RELEVANT city-wide only if BIG impact: load-shedding stages, city-wide water outages, weather warnings, major arterial closures (N1/N2 peak, M3), fires threatening residential zones, mountain rescues.
- NOT RELEVANT: incidents in [list far-off suburbs to reject explicitly, e.g. Durbanville, Bellville, Epping, Belhar, Elsies River, Kraaifontein, Mitchells Plain, Somerset West], sports scores, ads, generic content.
- Rule of thumb: if a {COMMUNITY} resident would say "nothing to do with me", reject.

VOICE: dry, honest, factual. Never invent details not in the source. Never use em-dashes, emoji, or exclamation marks.`;

export function CommunitySettings() {
    const { slug } = useParams<{ slug: string }>();
    const api = useApi();
    const { authed, login } = useAuth();

    const [topics, setTopics] = useState<Topic[]>([]);
    const loadTopics = useCallback(async () => {
        if (!authed || !slug) return;
        try {
            const r = await api.get<{ topics: Topic[] }>('/api/communities/' + slug + '/topics');
            setTopics(r.topics ?? []);
        } catch (e) { console.error('load topics', e); }
    }, [api, authed, slug]);
    useEffect(() => { loadTopics(); }, [loadTopics]);

    if (!authed) return (
        <div className="prelogin">
            <p>Log in to see community settings.</p>
            <button type="button" className="plain" onClick={login}>Log in</button>
        </div>
    );
    if (!slug) return null;

    return (
        <main style={{ padding: '20px 24px 60px', maxWidth: 1400, margin: '0 auto' }}>
            <div className="masthead" style={{ marginBottom: 12 }}>
                <div className="rule-top" />
                <h1>Settings · {slug}</h1>
                <div className="issue">Topics · Photos · Reporter · Editors · Boundary</div>
                <div className="rule-bottom" />
            </div>
            <nav style={navStyle}>
                <Link to={`/communities/${slug}`}           style={navLink}>← Read paper</Link>
                <Link to={`/communities/${slug}/newsroom`}  style={navLink}>Newsroom</Link>
                <Link to={`/communities/${slug}/dashboard`} style={navLink}>Dashboard (stats)</Link>
                <Link to={`/communities/${slug}/settings`}  style={{ ...navLink, fontWeight: 700 }}>Settings</Link>
                <Link to={`/communities/${slug}/boundary`}  style={{ ...navLink, marginLeft: 'auto' }}>Boundary ✏</Link>
            </nav>

            <SectionHeader title="Publishing" hint="Cadence draws the 'current edition' divider on the community page. Daily = today above the line, yesterday below. Weekly = this ISO week above, last week below. Monthly = this month above, prior months below. All boundaries anchored to SAST midnight." />
            <PublishingPanel slug={slug} api={api} />

            <SectionHeader title="Topics" hint="Daily-article buckets. Reporter tags each new item; multiple items with the same topic that day roll into today's article." />
            <TopicsPanel topics={topics} slug={slug} api={api} onChange={loadTopics} />

            <SectionHeader title="Photos" hint="Local imagery the reporter picks from before falling back to Pexels. Tag each with topic slugs (traffic, water, fire, mountain…)." />
            <PhotosPanel slug={slug} api={api} />

            <SectionHeader title="Reporter" hint="System prompt for this community's reporter agent. Tweak geography, tone, rejects." />
            <ReporterPanel slug={slug} api={api} />

            <SectionHeader title="Editors" hint="People who can approve/reject queue items for this community." />
            <ModeratorsPanel slug={slug} api={api} />

            <SectionHeader title="Boundary" hint="Polygon defining the community's coverage area. Used for geo-filtering feed items and crime-map plotting." />
            <div style={{ padding: 12, background: '#faf8f2', border: '1px solid #cfc9b7', marginBottom: 12 }}>
                <Link to={`/communities/${slug}/boundary`} className="primary" style={{ display: 'inline-block', padding: '6px 12px', textDecoration: 'none' }}>
                    Open boundary editor →
                </Link>
            </div>
        </main>
    );
}

function SectionHeader({ title, hint }: { title: string; hint: string }) {
    return (
        <div style={{ marginTop: 20, marginBottom: 6 }}>
            <div style={{ fontFamily: 'Newsreader, serif', fontSize: 22 }}>{title}</div>
            <div style={{ fontSize: 12, color: '#666' }}>{hint}</div>
        </div>
    );
}

// ---- Publishing ----

function PublishingPanel({ slug, api }: { slug: string; api: ReturnType<typeof useApi> }) {
    const [community, setCommunity] = useState<Community | null>(null);
    const [savedAt, setSavedAt] = useState<Date | null>(null);
    const [err, setErr] = useState('');

    const load = useCallback(async () => {
        try {
            const c = await api.get<Community>('/api/communities/' + slug);
            setCommunity(c);
        } catch (e) { setErr((e as Error).message); }
    }, [api, slug]);
    useEffect(() => { load(); }, [load]);

    const setCadence = useCallback(async (next: Cadence) => {
        if (!community || community.cadence === next) return;
        setErr('');
        try {
            const updated = await api.patch<Community>('/api/communities/' + slug, { cadence: next });
            setCommunity(updated);
            setSavedAt(new Date());
        } catch (e) { setErr((e as Error).message); }
    }, [api, community, slug]);

    if (!community) return <div style={{ padding: 12, color: '#666' }}>Loading…</div>;

    const options: Array<{ value: Cadence; label: string; blurb: string }> = [
        { value: 'daily',   label: 'Daily',   blurb: 'A new edition every SAST midnight.' },
        { value: 'weekly',  label: 'Weekly',  blurb: 'A new edition every Monday 00:00 SAST.' },
        { value: 'monthly', label: 'Monthly', blurb: 'A new edition on the 1st of each month, 00:00 SAST.' },
    ];

    return (
        <div style={{ padding: 12, background: '#faf8f2', border: '1px solid #cfc9b7', marginBottom: 12 }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {options.map(o => (
                    <label key={o.value} style={{ display: 'flex', gap: 8, alignItems: 'baseline', cursor: 'pointer' }}>
                        <input
                            type="radio"
                            name="cadence"
                            value={o.value}
                            checked={community.cadence === o.value}
                            onChange={() => setCadence(o.value)}
                        />
                        <span style={{ fontWeight: 600 }}>{o.label}</span>
                        <span style={{ color: '#666', fontSize: 13 }}>— {o.blurb}</span>
                    </label>
                ))}
            </div>
            {err     && <div style={{ color: '#8b1919', fontSize: 12, marginTop: 8 }}>{err}</div>}
            {savedAt && <div style={{ color: '#2b5e3f', fontSize: 12, marginTop: 8 }}>Saved {timeAgo(savedAt.toISOString())}</div>}
        </div>
    );
}

// ---- Topics ----

function TopicsPanel({ topics, slug, api, onChange }: {
    topics: Topic[]; slug: string; api: ReturnType<typeof useApi>; onChange: () => void;
}) {
    const [newSlug, setNewSlug] = useState('');
    const [newName, setNewName] = useState('');
    const [newMode, setNewMode] = useState<'activity'|'rewrite'>('activity');
    const [newPhoto, setNewPhoto] = useState('');
    async function create() {
        if (!newSlug || !newName) { alert('slug and name required'); return; }
        try {
            await api.post(`/api/communities/${slug}/topics`, {
                slug: newSlug, name: newName, mode: newMode,
                default_photo_query: newPhoto || null,
            });
            setNewSlug(''); setNewName(''); setNewPhoto(''); onChange();
        } catch (e) { alert('create failed: ' + (e as Error).message); }
    }
    async function patch(t: Topic, patch: Partial<Topic>) {
        try {
            await api.patch(`/api/communities/${slug}/topics/${t.slug}`, patch);
            onChange();
        } catch (e) { alert('patch failed: ' + (e as Error).message); }
    }
    return (
        <section style={{ padding: 12, background: '#faf8f2', border: '1px solid #cfc9b7', marginBottom: 12 }}>
            <div style={{ display: 'flex', gap: 6, marginBottom: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                <input placeholder="slug (e.g. traffic)" value={newSlug}
                    onChange={e => setNewSlug(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''))}
                    style={topicInput} />
                <input placeholder="Display name (e.g. Traffic)" value={newName}
                    onChange={e => setNewName(e.target.value)} style={{ ...topicInput, minWidth: 180 }} />
                <select value={newMode} onChange={e => setNewMode(e.target.value as 'activity'|'rewrite')} style={topicInput}>
                    <option value="activity">activity (append updates)</option>
                    <option value="rewrite">rewrite (regenerate body)</option>
                </select>
                <input placeholder="Pexels query (e.g. leaky tap)" value={newPhoto}
                    onChange={e => setNewPhoto(e.target.value)} style={{ ...topicInput, minWidth: 160 }} />
                <button type="button" className="primary" onClick={create}>+ add</button>
            </div>
            <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
                <thead>
                    <tr style={{ borderBottom: '1px solid #cfc9b7', textAlign: 'left' }}>
                        <th style={topicTh}>Slug</th><th style={topicTh}>Name</th>
                        <th style={topicTh}>Mode</th><th style={topicTh}>Photo query</th>
                        <th style={topicTh}>Active</th>
                    </tr>
                </thead>
                <tbody>
                    {topics.length === 0 && (
                        <tr><td colSpan={5} style={{ padding: 12, color: '#999', fontStyle: 'italic' }}>
                            No topics yet. Suggested starters: traffic, water, fire, load_shedding, weather, dogs.
                        </td></tr>
                    )}
                    {topics.map(t => (
                        <TopicRow key={t.slug} topic={t} onPatch={(p) => patch(t, p)} />
                    ))}
                </tbody>
            </table>
        </section>
    );
}

function TopicRow({ topic, onPatch }: { topic: Topic; onPatch: (p: Partial<Topic>) => void }) {
    const [q, setQ] = useState(topic.default_photo_query ?? '');
    const dirty = q !== (topic.default_photo_query ?? '');
    return (
        <tr style={{ borderBottom: '1px solid #eee', opacity: topic.active ? 1 : 0.5 }}>
            <td style={topicTd}><code>{topic.slug}</code></td>
            <td style={topicTd}>{topic.name}</td>
            <td style={topicTd}>
                <button type="button" className="plain"
                    onClick={() => onPatch({ mode: topic.mode === 'activity' ? 'rewrite' : 'activity' })}>
                    {topic.mode}
                </button>
            </td>
            <td style={topicTd}>
                <input value={q} onChange={e => setQ(e.target.value)}
                    placeholder="e.g. leaky tap"
                    style={{ ...topicInput, minWidth: 140 }}
                    onBlur={() => { if (dirty) onPatch({ default_photo_query: q }); }}
                />
            </td>
            <td style={topicTd}>
                <input type="checkbox" checked={topic.active}
                    onChange={() => onPatch({ active: !topic.active })} />
            </td>
        </tr>
    );
}

// ---- Reporter ----

function ReporterPanel({ slug, api }: { slug: string; api: ReturnType<typeof useApi> }) {
    const [state, setState] = useState<ReporterSettings | null>(null);
    const [prompt, setPrompt] = useState('');
    const [saving, setSaving] = useState(false);

    const load = useCallback(async () => {
        try {
            const r = await api.get<ReporterSettings>(`/api/communities/${slug}/reporter`);
            setState(r);
            setPrompt(r.system_prompt ?? '');
        } catch (e) { console.error('load reporter', e); }
    }, [api, slug]);
    useEffect(() => { load(); }, [load]);

    async function save() {
        setSaving(true);
        try {
            await api.patch(`/api/communities/${slug}/reporter`, { system_prompt: prompt });
            load();
        } catch (e) { alert('save failed: ' + (e as Error).message); }
        finally { setSaving(false); }
    }

    if (!state) return null;
    if (!state.exists) {
        return (
            <section style={{ padding: 12, background: '#faf8f2', border: '1px solid #cfc9b7', marginBottom: 12 }}>
                <p style={{ margin: 0, fontSize: 13, color: '#666' }}>
                    No reporter agent for this community yet. Ask a staff user to create one at /admin/ai-agents.
                </p>
            </section>
        );
    }
    const dirty = prompt !== (state.system_prompt ?? '');
    return (
        <section style={{ padding: 12, background: '#faf8f2', border: '1px solid #cfc9b7', marginBottom: 12 }}>
            <div style={{ display: 'flex', alignItems: 'baseline', marginBottom: 6, gap: 12 }}>
                <strong style={{ fontSize: 13 }}>{state.agent_name}</strong>
                <span style={{ fontSize: 11, color: '#666' }}>
                    {state.active ? 'active' : 'paused'}
                    {state.paused_reason && ` — ${state.paused_reason}`} · model {state.model}
                </span>
                <span style={{ marginLeft: 'auto', fontSize: 11, color: '#666' }}>
                    Reporter uses this prompt for every feed item.
                </span>
            </div>
            <textarea value={prompt} onChange={e => setPrompt(e.target.value)}
                rows={16}
                placeholder={REPORTER_PROMPT_TEMPLATE.replace(/\{COMMUNITY\}/g, slug)}
                style={{ width: '100%', fontFamily: 'monospace', fontSize: 12, padding: 8, border: '1px solid #cfc9b7' }}
            />
            <div style={{ display: 'flex', gap: 8, marginTop: 8, alignItems: 'center' }}>
                <button type="button" className="primary" disabled={!dirty || saving} onClick={save}>
                    {saving ? 'Saving…' : 'Save prompt'}
                </button>
                <button type="button" className="plain" disabled={saving}
                    onClick={() => setPrompt(REPORTER_PROMPT_TEMPLATE.replace(/\{COMMUNITY\}/g, slug))}>
                    Load starter template
                </button>
                {dirty && <span style={{ fontSize: 11, color: '#8b1919' }}>unsaved changes</span>}
                <span style={{ marginLeft: 'auto', fontSize: 11, color: '#666' }}>
                    Blank = uses built-in default. Save empty to reset.
                </span>
            </div>
        </section>
    );
}

// ---- Photos ----

function PhotosPanel({ slug, api }: { slug: string; api: ReturnType<typeof useApi> }) {
    const [photos, setPhotos] = useState<StockPhoto[]>([]);
    const [allTags, setAllTags] = useState<string[]>([]);
    const [uploading, setUploading] = useState(0);
    const [dragOver, setDragOver] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const load = useCallback(async () => {
        try {
            const r = await api.get<{ photos: StockPhoto[]; all_tags: string[] }>(
                `/api/communities/${slug}/photos`);
            setPhotos(r.photos ?? []);
            setAllTags(r.all_tags ?? []);
        } catch (e) { console.error('load photos', e); }
    }, [api, slug]);
    useEffect(() => { load(); }, [load]);

    async function uploadFiles(files: FileList | File[]) {
        const list = Array.from(files).filter(f => f.type.startsWith('image/'));
        if (list.length === 0) return;
        setUploading(list.length);
        for (const file of list) {
            try {
                const form = new FormData();
                form.append('file', file);
                const res = await api.postForm<{ url: string }>('/api/uploads', form);
                await api.post(`/api/communities/${slug}/photos`, { media_url: res.url, tags: [] });
            } catch (e) {
                alert(`upload of ${file.name} failed: ${(e as Error).message}`);
            }
            setUploading(n => n - 1);
        }
        load();
    }

    async function updatePhoto(id: string, patch: Partial<StockPhoto>) {
        try {
            await api.patch(`/api/communities/${slug}/photos/${id}`, patch);
            load();
        } catch (e) { alert('update failed: ' + (e as Error).message); }
    }
    async function deletePhoto(id: string) {
        if (!confirm('Delete this photo permanently?')) return;
        try {
            await api.del(`/api/communities/${slug}/photos/${id}`);
            load();
        } catch (e) { alert('delete failed: ' + (e as Error).message); }
    }

    return (
        <section style={{ padding: 12, background: '#faf8f2', border: '1px solid #cfc9b7', marginBottom: 12 }}>
            <div
                onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={e => {
                    e.preventDefault(); setDragOver(false);
                    if (e.dataTransfer.files) uploadFiles(e.dataTransfer.files);
                }}
                style={{
                    padding: '16px', textAlign: 'center', cursor: 'pointer',
                    border: `2px dashed ${dragOver ? '#8b1919' : '#cfc9b7'}`,
                    background: dragOver ? '#fff5f5' : '#fff',
                    marginBottom: 12,
                }}
                onClick={() => fileInputRef.current?.click()}
            >
                <input ref={fileInputRef} type="file" accept="image/*" multiple
                    style={{ display: 'none' }}
                    onChange={e => e.target.files && uploadFiles(e.target.files)} />
                <div style={{ fontSize: 14, color: '#666' }}>
                    Drag & drop images here, or click to pick files
                </div>
                {uploading > 0 && (
                    <div style={{ marginTop: 8, fontSize: 12, color: '#8b1919' }}>
                        Uploading {uploading}…
                    </div>
                )}
            </div>

            {photos.length === 0 ? (
                <div style={{ padding: 20, color: '#999', fontStyle: 'italic', textAlign: 'center', fontSize: 13 }}>
                    No photos yet. Upload local imagery so the reporter picks these before Pexels.
                </div>
            ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 12 }}>
                    {photos.map(p => (
                        <PhotoCard key={p.id} photo={p} allTags={allTags}
                            onSave={(patch) => updatePhoto(p.id, patch)}
                            onDelete={() => deletePhoto(p.id)} />
                    ))}
                </div>
            )}
        </section>
    );
}

function PhotoCard(p: {
    photo: StockPhoto; allTags: string[];
    onSave: (patch: Partial<StockPhoto>) => void; onDelete: () => void;
}) {
    const [tags, setTags] = useState<string[]>(p.photo.tags);
    const [caption, setCaption] = useState(p.photo.caption ?? '');
    const dirty = JSON.stringify(tags) !== JSON.stringify(p.photo.tags) ||
                  caption !== (p.photo.caption ?? '');

    return (
        <div style={{ border: '1px solid #cfc9b7', background: '#fff', opacity: p.photo.active ? 1 : 0.4 }}>
            <img src={p.photo.media_url} alt="" loading="lazy"
                style={{ width: '100%', height: 140, objectFit: 'cover', display: 'block' }} />
            <div style={{ padding: 8 }}>
                <TagInput tags={tags} setTags={setTags} suggestions={p.allTags} />
                <input type="text" value={caption}
                    onChange={e => setCaption(e.target.value)}
                    placeholder="Caption (optional)"
                    style={{ width: '100%', padding: 4, fontSize: 12, marginTop: 6, border: '1px solid #cfc9b7' }} />
                <div style={{ display: 'flex', gap: 6, marginTop: 6, fontSize: 11, color: '#666', alignItems: 'center' }}>
                    <span title="How many posts have used this photo">used {p.photo.usage_count}×</span>
                    <button type="button" className="plain"
                        onClick={() => p.onSave({ active: !p.photo.active })}
                        style={{ marginLeft: 'auto', fontSize: 11 }}>
                        {p.photo.active ? 'disable' : 'enable'}
                    </button>
                    <button type="button" className="plain"
                        onClick={p.onDelete} style={{ fontSize: 11, color: '#8b1919' }}>delete</button>
                </div>
                {dirty && (
                    <button type="button" className="primary"
                        onClick={() => p.onSave({ tags, caption: caption || null })}
                        style={{ marginTop: 6, width: '100%', padding: '4px 8px', fontSize: 12 }}>
                        Save changes
                    </button>
                )}
            </div>
        </div>
    );
}

function TagInput(p: { tags: string[]; setTags: (t: string[]) => void; suggestions: string[] }) {
    const [input, setInput] = useState('');
    const [focused, setFocused] = useState(false);
    const normalized = input.toLowerCase().replace(/[^a-z0-9_]/g, '');
    const matches = normalized
        ? p.suggestions.filter(s => s.includes(normalized) && !p.tags.includes(s)).slice(0, 6)
        : [];

    function addTag(t: string) {
        const norm = t.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
        if (!norm || p.tags.includes(norm)) return;
        p.setTags([...p.tags, norm]);
        setInput('');
    }
    function removeTag(t: string) {
        p.setTags(p.tags.filter(x => x !== t));
    }

    return (
        <div style={{ position: 'relative' }}>
            <div style={{
                display: 'flex', flexWrap: 'wrap', gap: 4, padding: 4,
                border: '1px solid #cfc9b7', background: '#fff', minHeight: 30,
            }}>
                {p.tags.map(t => (
                    <span key={t} style={tagChip}>
                        {t}
                        <button type="button" onClick={() => removeTag(t)}
                            style={{ background: 'none', border: 'none', cursor: 'pointer',
                                     marginLeft: 4, fontSize: 11, color: '#8b1919', padding: 0 }}>×</button>
                    </span>
                ))}
                <input type="text" value={input}
                    onChange={e => setInput(e.target.value)}
                    onFocus={() => setFocused(true)}
                    onBlur={() => setTimeout(() => setFocused(false), 120)}
                    onKeyDown={e => {
                        if (e.key === 'Enter' || e.key === ',') {
                            e.preventDefault();
                            addTag(input);
                        }
                        if (e.key === 'Backspace' && input === '' && p.tags.length > 0) {
                            removeTag(p.tags[p.tags.length - 1]);
                        }
                    }}
                    placeholder={p.tags.length === 0 ? 'add tags…' : ''}
                    style={{ flex: 1, minWidth: 80, border: 'none', outline: 'none', fontSize: 12 }} />
            </div>
            {focused && matches.length > 0 && (
                <div style={{
                    position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 5,
                    background: '#fff', border: '1px solid #cfc9b7',
                    maxHeight: 160, overflowY: 'auto',
                }}>
                    {matches.map(m => (
                        <div key={m}
                            onMouseDown={e => { e.preventDefault(); addTag(m); }}
                            style={{ padding: '4px 8px', fontSize: 12, cursor: 'pointer' }}
                            onMouseEnter={e => e.currentTarget.style.background = '#f2eee0'}
                            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                            {m}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

// ---- Editors (moderators) ----

function ModeratorsPanel({ slug, api }: { slug: string; api: ReturnType<typeof useApi> }) {
    const [mods, setMods] = useState<Moderator[]>([]);
    const [err,  setErr]  = useState('');
    const [addUserID, setAddUserID] = useState('');

    const load = useCallback(async () => {
        try {
            const r = await api.get<{ moderators: Moderator[] }>('/api/communities/' + slug + '/moderators');
            setMods(r.moderators ?? []);
        } catch (e) { setErr((e as Error).message); }
    }, [api, slug]);
    useEffect(() => { load(); }, [load]);

    async function remove(uid: string) {
        try {
            await api.del('/api/communities/' + slug + '/moderators/' + uid);
            await load();
        } catch (e) { setErr((e as Error).message); }
    }
    async function add() {
        if (!addUserID) return;
        setErr('');
        try {
            await api.post('/api/communities/' + slug + '/moderators', { user_id: addUserID });
            setAddUserID('');
            await load();
        } catch (e) { setErr((e as Error).message); }
    }

    return (
        <section style={{ padding: 12, background: '#faf8f2', border: '1px solid #cfc9b7', marginBottom: 12 }}>
            {err && <p style={{ color: '#8b1919' }}>{err}</p>}
            {mods.length === 0 ? (
                <p className="empty">No editors yet.</p>
            ) : mods.map(m => (
                <div className="member-row" key={m.user_id}>
                    <span className="member-name">{m.display_name}</span>
                    <span className="member-joined">since {timeAgo(m.granted_at)}</span>
                    <span className="group-role role-admin">{m.role}</span>
                    <button type="button" className="link danger" onClick={() => remove(m.user_id)}>Remove</button>
                </div>
            ))}
            <div className="mod-add" style={{ marginTop: 10 }}>
                <input
                    type="text"
                    placeholder="Add editor by user_id (UUID)"
                    value={addUserID}
                    onChange={(e) => setAddUserID(e.target.value)}
                />
                <button type="button" className="primary" onClick={add}>Add</button>
                <em style={{ display: 'block', marginTop: 4, fontSize: 11, color: '#666' }}>
                    Community + friend-search lookup lands in Phase 2. For now paste the target's user_id.
                </em>
            </div>
        </section>
    );
}

// ---- shared styles ----

const navStyle: React.CSSProperties = {
    display: 'flex', gap: 12, padding: '8px 0', marginBottom: 12,
    borderBottom: '1px solid #cfc9b7',
    fontFamily: '-apple-system, sans-serif',
    fontSize: 12, textTransform: 'uppercase', letterSpacing: 1,
    alignItems: 'center',
};
const navLink: React.CSSProperties = { color: '#333', textDecoration: 'none', padding: '4px 10px' };
const topicInput: React.CSSProperties = { padding: '4px 8px', fontSize: 12, border: '1px solid #cfc9b7' };
const topicTh: React.CSSProperties = { padding: '4px 8px', fontSize: 11, textTransform: 'uppercase', letterSpacing: 1 };
const topicTd: React.CSSProperties = { padding: '4px 8px' };
const tagChip: React.CSSProperties = {
    display: 'inline-flex', alignItems: 'center',
    padding: '2px 6px', fontSize: 11,
    background: '#f2eee0', border: '1px solid #cfc9b7',
    borderRadius: 2,
};
